<script setup>
import { useStore } from "vuex";
import { computed, ref } from "vue";
import { trackPrintDesignScaled, trackBackgroundToggle } from "../utils/tracker";
import ImageReviewMeasurement from "./ImageReviewMeasurement.vue";
import ImageReviewImage from "./ImageReviewImage.vue";
import ImageReviewControls from "./ImageReviewControls.vue";
import ImageReviewEnhancing from "./ImageReviewEnhancing.vue";
import ImageReviewEnhanced from "./ImageReviewEnhanced.vue";
import ImageReviewAsis from "./ImageReviewAsis.vue";
import ImageReviewBgToggle from "./ImageReviewBgToggle.vue";
import ImageReviewTitles from "./ImageReviewTitles.vue";
import ImageReviewRequest from "./ImageReviewRequest.vue";
import ImageReviewLegend from "./imageReviewLegend.vue";

const asisShown = ref(false);
const loadingShown = ref(false);
const imageReviewComponent = ref(null);
const scale = ref(1);
const legendShown = ref(true);

const store = useStore();
const config = computed(() => store.getters.config);
const enhancePreselected = computed(() => store.getters["artwork/enhancePreselected"]);
const artwork = computed(() => store.getters["artwork/artwork"]);
const artworkSize = computed(() => store.getters["artwork/artworkSize"]);
const artworkUrl = computed(() => store.getters["artwork/artworkUrl"]);
const artworkLoading = computed(() => store.getters["loading/uploadArtwork"]);
const enhancedShown = computed(
  () => artwork.value && !artworkLoading.value && enhancePreselected.value
);
const artworkExists = computed(() => store.getters["artwork/artworkExists"]);
const imageUrl = computed(() => store.getters["artwork/artworkUrl"]);
const enhanced = computed(() => store.getters["artwork/enhanced"]);
const comment = computed(() => store.getters["artwork/comment"]);
const bgRemoved = computed(() => store.getters["artwork/removeBackground"]);
const toggleShown = computed(
  () => artwork.value?.metadata?.can_remove_bg && enhanced.value && !artworkLoading.value
);
const bgStyle = computed(() => {
  const { swatchUrl, colorHex } = store.getters["product/selectedComposition"] || {};
  return {
    background: swatchUrl ? `url(${swatchUrl})` : colorHex
  };
});
const editType = computed(() => store.getters["artwork/editType"]);
const price = computed(() => {
  const prices = store.getters["product/prices"];
  try {
    return Object.values(Object.values(Object.values(prices)[0]))[0][0];
  } catch (_) {
    return 0;
  }
});

const oneInch = computed(() => {
  const canvas = imageReviewComponent.value;
  if (!canvas) return 0;
  // 1inch is equal 35% of canvas width. It's a magic number.
  const onInchInPercentage = 35;
  return Math.min(Math.floor((onInchInPercentage * canvas.clientWidth) / 100), 131);
});
const pxToInch = size => store.getters["artwork/convertPxToInches"](size);
const initWidth = computed(() => pxToInch(artworkSize.value.width) * oneInch.value);

const useAsis = () => {
  store.commit("artwork/setEnhancePreselected", true);
  if (enhancedShown.value) {
    store.commit("artwork/setArtworkEnhanced", false);
  } else {
    store.commit("artwork/setTemporalEnhanced", false);
    asisShown.value = true;
  }
};

const useEnhancement = () => {
  store.commit("artwork/setEnhancePreselected", true);
  if (enhancedShown.value) {
    store.commit("artwork/setArtworkEnhanced", true);
  } else {
    store.commit("artwork/setTemporalEnhanced", true);
    loadingShown.value = true;
  }
};

const clearChoose = () => {
  asisShown.value = false;
  loadingShown.value = false;
};

const removeBackgroundChange = e => {
  store.commit("artwork/setRemoveBackground", e.target.checked);
  store.commit("checkout/setCurrentPlacementAction", "bg_toggle");

  trackBackgroundToggle(artwork.value, price.value, e.target.checked);
};

const updateSize = scaledImageValue => {
  if (!scaledImageValue || !Number.isFinite(scaledImageValue)) return;

  const oldValue = scale.value;
  scale.value = scaledImageValue * oneInch.value;

  if (oldValue !== scale.value && oldValue !== 1) legendShown.value = false;

  trackPrintDesignScaled({
    artwork_url: artworkUrl.value,
    old_scale: oldValue,
    new_scale: scale.value,
    artwork: artwork.value,
    price: price.value,
    editType: editType.value
  });
};

const saveComment = commentVal => {
  if (enhancedShown.value) {
    store.commit("artwork/setArtworkComment", commentVal);
    store.dispatch("checkout/updatePlacement");
  } else {
    store.commit("artwork/setTemporalComment", commentVal);
  }
};
</script>

<template>
  <div ref="imageReviewComponent" class="image-review">
    <div class="image-review__title">Image review</div>

    <div v-if="artworkExists" data-testid="image-review-main-wrapper">
      <div class="image-review__intro">
        Check out your design and make sure it's on point! <br />
        If not, try our AI Image Enhancement or send a note to our designers to make it just right!
      </div>

      <div class="image-review__background-wrapper">
        <div v-if="enhancedShown" class="image-review__titles" data-testid="image-review-titles">
          <ImageReviewTitles :enhanced="enhanced" />
        </div>

        <div class="image-review__image">
          <ImageReviewMeasurement :one-inch="oneInch" :scale="scale">
            <ImageReviewImage
              :artwork-size="artworkSize"
              :init-width="initWidth"
              :image-url="imageUrl"
              :bg-style="bgStyle"
              @update:zoom="updateSize"
            />
          </ImageReviewMeasurement>
        </div>

        <ImageReviewLegend v-if="legendShown" data-testid="image-review-legend" />
      </div>

      <div class="image-review__overlap-content">
        <div v-if="toggleShown" class="image-review__bg-toggle" data-testid="image-review-toggles">
          <ImageReviewBgToggle :bg-removed="bgRemoved" @change-bg="removeBackgroundChange" />
        </div>

        <div class="image-review__controls">
          <ImageReviewAsis
            v-if="asisShown"
            data-testid="image-review-asis"
            @clear-choose="clearChoose"
          />

          <ImageReviewEnhanced
            v-else-if="enhancedShown"
            data-testid="image-review-enhanced"
            :preselected="enhancePreselected"
            :enhanced="enhanced"
            @use-as-is="useAsis"
            @use-enhancement="useEnhancement"
          />

          <ImageReviewEnhancing
            v-else-if="loadingShown"
            data-testid="image-review-loading"
            @cancel="clearChoose"
          />

          <ImageReviewControls
            v-else
            data-testid="image-review-controls"
            @use-as-is="useAsis"
            @use-enhance="useEnhancement"
          />

          <ImageReviewRequest
            v-if="config.redraw_note_jiffymade"
            data-testid="image-review-request"
            class="image-review__request"
            :comment="comment"
            @save-comment="saveComment"
          />
        </div>
      </div>
    </div>
    <div v-else class="image-review__intro" data-testid="image-review-preview-wrapper">
      Please upload your design to see a preview.
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.image-review {
  position: relative;
  z-index: 1; // To overlap with the next section

  @include with-desktop {
    border-radius: 8px;
    border: 1px solid $pod-gray-modern-200;
    background: $white;
    box-shadow: $pod-card-shadow;
    padding: 20px 12px;
  }

  &__title {
    font-size: 20px;
    font-weight: 500;
    margin: 0 $indent-mobile-constructor 7px;

    @include with-desktop {
      margin-bottom: 12px;
      font-size: 24px;
      text-align: center;
    }
  }

  &__intro {
    margin: 0 $indent-mobile-constructor 20px;
    border-radius: 6px;
    padding: 6px;
    font-size: 13px;
    background: $pog-light-fog-gradient;

    @include with-desktop {
      text-align: center;

      br {
        display: none;
      }
    }
  }

  &__intro-strong {
    font-weight: 600;
  }

  &__background-wrapper {
    position: relative;
    z-index: 0;
    padding-top: 100%;
    background-color: $pod-gray-modern-100;

    @include with-desktop {
      padding-top: 0;
      height: 658px;
    }
  }

  &__image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 64px 0;
  }

  &__overlap-content {
    position: relative;
    z-index: 1;
    margin: -50px $indent-mobile-constructor 0;
  }

  &__bg-toggle {
    display: flex;
    justify-content: center;
  }

  &__titles {
    position: absolute;
    z-index: 1;
    top: 16px;
    left: 0;
    right: 0;
  }

  &__controls {
    margin-top: 13px;
    padding: 15px 18px 18px;
    border-radius: 8px;
    background: $white;
    box-shadow:
      0 238px 67px 0 rgba(0, 0, 0, 0),
      0 152px 61px 0 rgba(0, 0, 0, 0.01),
      0 86px 51px 0 rgba(0, 0, 0, 0.05),
      0px -5px 38px 0px rgba(0, 0, 0, 0.09),
      0px 10px 21px 0px rgba(0, 0, 0, 0.1);
  }

  &__request {
    margin-top: 20px;
  }
}
</style>
